import { useUser } from '@clerk/clerk-react'
import { useStatsigClient } from '@statsig/react-bindings'
import {
  IconChevronRight,
  IconClockCode,
  IconLayoutSidebar,
  IconLayoutSidebarFilled,
} from '@tabler/icons-react'
import * as React from 'react'
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { PRODUCT_RELEASE_LIST_PATH } from '@/utility/productReleasePaths'
import { clearShowAppAlertFlag, hasPendingUnsavedNavigation } from '@/utility/showAppAlertStorage'
import { useUserPreferences } from '../../context/UserPreferencesContext'
import { INSIGHTS_VIEWS_ENABLED } from '../../pages/InsightsDashboard/insightsFilterMapping'
import { RELEASE_GATE_V2_13_0 } from '../../utility/releaseStatsigGateConstants.js'
import AlertBox from '../AlertBox/AlertBox'
import ToolTip from '../ToolTip/ToolTip'
import TimeZones from './timezones.json'

const HeaderBar = ({
  menu,
  menuNavigation,
  subMenuNavigation,
  subMenu,
  feature,
  headerExpand,
  setHeaderExpand,
  rightComponent,
  leftComponent,
  hideTimezone = false,
  transparentBackground,
  disableEvents,
  hideBreadcrumb,
  darkThemeHeaderBar,
  whiteHeaderBar = false,
  searchPopupProp,
  onDiscardUnsavedChanges,
  ..._props
}) => {
  const { timezone } = useUserPreferences()
  const { user } = useUser()
  const navigate = useNavigate()
  const location = useLocation()
  const { client: statsigClient } = useStatsigClient()
  const [showAppAlert, setShowAppAlert] = useState({ show: false, path: '' })

  const uagEnhancements = statsigClient.checkGate(RELEASE_GATE_V2_13_0)

  const urlLocation = location.pathname

  const getCurrentOffset = (timeZone) => {
    const now = new Date()
    const utcTime = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' }))
    const localTime = new Date(now.toLocaleString('en-US', { timeZone }))
    const offsetInMinutes = (localTime.getTime() - utcTime.getTime()) / (1000 * 60)
    return offsetInMinutes / 60 // Convert to hours
  }

  const formatGMTOffset = (offset) => {
    const sign = offset >= 0 ? '+' : '-'
    const absOffset = Math.abs(offset)
    const hours = Math.floor(absOffset)
    const minutes = (absOffset - hours) * 60

    if (minutes === 0) {
      return `UTC${sign}${hours}`
    }
    return `UTC${sign}${hours}:${minutes.toString().padStart(2, '0')}`
  }

  const getTimezoneAbbreviation = (timeZone) => {
    try {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone,
        timeZoneName: 'short',
      })
      const parts = formatter.formatToParts(new Date())

      if (getCurrentOffset(timeZone) > 0) {
        return TimeZones?.find((tz) => tz.value === timeZone)?.code || ''
      }
      return parts.find((part) => part.type === 'timeZoneName')?.value || ''
    } catch (_e) {
      return ''
    }
  }

  const timezoneMeta = useMemo(() => {
    if (!timezone) return null
    const tzInfo = TimeZones?.find((tz) => tz.value === timezone)
    const abbreviation = getTimezoneAbbreviation(timezone)
    const offset = formatGMTOffset(getCurrentOffset(timezone))
    return {
      label: tzInfo?.label || timezone,
      city: tzInfo?.city || '',
      abbreviation,
      offset,
    }
  }, [timezone])

  const [currentTimeLabel, setCurrentTimeLabel] = useState('')

  useEffect(() => {
    if (!timezone) return undefined

    const updateTimeLabel = () => {
      setCurrentTimeLabel(
        new Date().toLocaleTimeString('en-US', {
          timeZone: timezone,
          hour: 'numeric',
          minute: '2-digit',
          hour12: false,
        }),
      )
    }

    updateTimeLabel()
    const intervalId = window.setInterval(updateTimeLabel, 30_000)
    return () => window.clearInterval(intervalId)
  }, [timezone])

  const headerBarBackgroundClass = transparentBackground
    ? 'bg-transparent'
    : darkThemeHeaderBar
      ? 'bg-BG-Default-neutral-06'
      : whiteHeaderBar
        ? 'bg-BG-Default-white'
        : 'bg-BG-Default-neutral-00'

  return (
    <div
      data-testid="header-bar"
      className={`relative flex w-full items-center justify-between p-4 ${headerBarBackgroundClass} ${
        transparentBackground ? (searchPopupProp ? 'z-[10]' : 'z-[11]') : 'z-[12]'
      }`}
      id="header-bar"
    >
      {showAppAlert.show && (
        <AlertBox
          title="You have unsaved changes."
          message="Are you sure you want to leave this page? Your changes will be lost."
          confirmBtnText="Confirm"
          confirmBtnType="primary"
          cancelBtnType="secondary-v3"
          onClose={() => {
            setShowAppAlert(false)
          }}
          onConfirm={() => {
            if (onDiscardUnsavedChanges && typeof onDiscardUnsavedChanges === 'function') {
              onDiscardUnsavedChanges()
            }
            setShowAppAlert((prev) => ({ ...prev, show: false, path: '' }))
            clearShowAppAlertFlag()
            if (showAppAlert.path === window.location.pathname) {
              window.location.reload()
            } else {
              navigate({
                pathname: showAppAlert.path,
                search: '',
                replace: true,
              })
            }
          }}
          data-testid="header-bar-system-navigate-alert"
        />
      )}
      <div
        data-testid="header-bar-left"
        className="flex flex-nowrap items-center h-[36px] overflow-hidden w-[calc(100%-300px)] min-w-0"
      >
        {headerExpand ? (
          <IconLayoutSidebarFilled
            data-testid="header-bar-collapse-sidebar-icon"
            size={30}
            className={`${
              darkThemeHeaderBar
                ? 'text-Icon-strong-06 hover:text-Icon-strong-06'
                : 'text-Icon-strong-06'
            } rounded-md p-1 ${disableEvents ? '' : 'cursor-pointer hover:bg-BG-Default-white'} ${
              transparentBackground ? 'z-[10]' : ''
            } shrink-0`}
            stroke={2}
            onClick={() => !disableEvents && setHeaderExpand(false)}
          />
        ) : (
          <IconLayoutSidebar
            data-testid="header-bar-expand-sidebar-icon"
            size={30}
            className={`${
              darkThemeHeaderBar
                ? 'text-Icon-strong-06 hover:text-Icon-strong-06'
                : 'text-Icon-strong-06'
            } rounded-md p-1 ${disableEvents ? '' : 'cursor-pointer hover:bg-BG-Default-white'} ${
              transparentBackground ? 'z-[10]' : ''
            } shrink-0`}
            stroke={2}
            onClick={() => !disableEvents && setHeaderExpand(true)}
          />
        )}
        <div
          data-testid="header-bar-breadcrumb-divider"
          className={`border-l ${
            darkThemeHeaderBar ? 'border-white' : 'border-Border-Default-neutral-02'
          } mx-4 h-6 ${transparentBackground ? 'z-[10]' : ''}`}
        ></div>
        {menu && !hideBreadcrumb && (
          <div
            data-testid="header-menu-title"
            className={`text-sm font-semibold ${
              darkThemeHeaderBar ? 'text-white' : 'text-Text-medium-06'
            } ${transparentBackground ? 'z-[10]' : ''} min-w-fit`}
          >
            {menuNavigation ? (
              <span
                className="cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation()
                  if (hasPendingUnsavedNavigation()) {
                    setShowAppAlert((prev) => ({
                      ...prev,
                      show: true,
                      path: menuNavigation,
                    }))
                    return false
                  } else {
                    setShowAppAlert((prev) => ({
                      ...prev,
                      show: false,
                      path: '',
                    }))
                    navigate(menuNavigation)
                  }
                }}
              >
                {menu}
              </span>
            ) : (
              menu
            )}
          </div>
        )}
        {subMenu && !hideBreadcrumb && (
          <React.Fragment>
            <IconChevronRight
              data-testid="header-bar-submenu-chevron"
              className={`${darkThemeHeaderBar ? 'text-white' : 'text-Icon-strong-06'} mx-4 h-5`}
              stroke={2}
            />
            <div
              data-testid="header-submenu-title"
              className={`text-sm font-semibold ${
                darkThemeHeaderBar ? 'text-white' : 'text-Text-medium-06'
              } ${transparentBackground ? 'z-[10]' : ''} max-w-[200px] truncate`}
            >
              {subMenuNavigation ? (
                <span
                  className="cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation()
                    if (hasPendingUnsavedNavigation()) {
                      setShowAppAlert((prev) => ({
                        ...prev,
                        show: true,
                        path: subMenuNavigation,
                      }))
                      return false
                    } else {
                      setShowAppAlert((prev) => ({
                        ...prev,
                        show: false,
                        path: '',
                      }))
                      navigate(subMenuNavigation)
                    }
                  }}
                >
                  {subMenu}
                </span>
              ) : (
                subMenu
              )}
            </div>
          </React.Fragment>
        )}
        {feature && !hideBreadcrumb && (
          <React.Fragment>
            <IconChevronRight
              data-testid="header-bar-feature-chevron"
              className={`${darkThemeHeaderBar ? 'text-white' : 'text-Icon-strong-06'} mx-4 h-5`}
              stroke={2}
            />
            <div
              data-testid="header-feature-title"
              className={`text-sm font-semibold whitespace-nowrap shrink-0 ${
                darkThemeHeaderBar ? 'text-white' : 'text-Text-medium-06'
              } ${transparentBackground ? 'z-[10]' : ''}`}
            >
              {feature}
            </div>
          </React.Fragment>
        )}
        {(leftComponent || (urlLocation?.includes?.('/insights') && INSIGHTS_VIEWS_ENABLED)) && (
          <React.Fragment>
            {menu && !hideBreadcrumb && (
              <div
                data-testid="header-bar-left-component-divider"
                className={`border-l ${
                  darkThemeHeaderBar
                    ? 'border-Border-Default-neutral-10'
                    : 'border-Border-Default-neutral-02'
                } mx-4 h-6 ${transparentBackground ? 'z-[10]' : ''}`}
              ></div>
            )}
            {leftComponent || (
              <div
                id="header-left-portal"
                className="flex items-center gap-2 w-[calc(100%-62px)] z-[15]"
                data-testid="header-left-portal"
              />
            )}
          </React.Fragment>
        )}
      </div>
      <div
        data-testid="header-notifications"
        className={`flex justify-end items-center h-[36px] overflow-hidden shrink-0 ${
          [
            '/shipment/commandcenter',
            '/shipment/live',
            '/shipment/completed',
            '/shipment/temperature',
            PRODUCT_RELEASE_LIST_PATH,
          ].includes(urlLocation)
            ? 'w-[375px]'
            : ''
        }`}
      >
        {rightComponent && <React.Fragment>{rightComponent}</React.Fragment>}
        <div
          id="header-right-portal"
          className="flex items-center gap-2"
          data-testid="header-right-portal"
        />
        {!hideTimezone && timezoneMeta && (
          <ToolTip
            text={
              <div className="flex flex-col items-center justify-center">
                <span>Your timezone</span>
                <span className="text-center">
                  {timezoneMeta.label} {timezoneMeta.city && `(${timezoneMeta.city})`} .{' '}
                  {timezoneMeta.abbreviation} ({timezoneMeta.offset})
                </span>
              </div>
            }
          >
            <div
              className="flex items-center ml-3 bg-BG-Default-white rounded-md px-2.5 py-1.5 cursor-pointer border border-Border-Default-neutral-01 hover:border-Border-Selected-brand-02 shadow-sm"
              onClick={() => {
                if (hasPendingUnsavedNavigation()) {
                  setShowAppAlert((prev) => ({
                    ...prev,
                    show: true,
                    path: uagEnhancements ? `/user/${user.id}/preferences` : '/manage/timezone',
                  }))
                  return false
                } else {
                  setShowAppAlert((prev) => ({
                    ...prev,
                    show: false,
                    path: '',
                  }))
                }
                if (uagEnhancements) {
                  navigate(`/user/${user.id}/preferences`)
                } else {
                  navigate('/manage/timezone')
                }
              }}
            >
              <IconClockCode size={18} className="text-Icon-strong-06" data-testid="clock-icon" />
              <span
                className="ml-2 text-sm font-semibold text-Text-medium-06 whitespace-nowrap"
                data-testid="header-bar-timezone-display"
              >
                {timezoneMeta.offset} ({timezoneMeta.abbreviation})
                {currentTimeLabel ? (
                  <>
                    <span className="mx-1.5 text-Text-placeholder" aria-hidden="true">
                      ·
                    </span>
                    {currentTimeLabel}
                  </>
                ) : null}
              </span>
            </div>
          </ToolTip>
        )}
      </div>
    </div>
  )
}

export default HeaderBar
